'use client';

import React, { useEffect, useState } from 'react';
import { Avatar, ConfigProvider, Dropdown, Layout, MenuProps, Modal, theme } from 'antd';
import { useTheme } from 'next-themes';
const { Header } = Layout;
import './styles/Navbar.css';
import {
	modalError,
	modalSuccess,
} from '@/utils/ui/wrapper/NotificationWrapper';
import { jwtDecode } from 'jwt-decode';
import { useRouter } from 'next/navigation';
import useUserStore from '../../store/zustand/decode-zustand';
import { dsTheme } from '@/styles/dsTheme';

// Nombre de un icono de Material Symbols Rounded (02_lineamientos_visuales.md #2), mismo lenguaje
// visual que ya usan el Sidebar y el Dashboard (AB#10842).
const Icon = (name: string, size = 18) => (
	<span className="material-symbols-rounded" style={{ fontSize: size }}>{name}</span>
);

const DashboardNavbar: React.FC = () => {
	const [usuario, setUsuario] = useState<string | undefined>();
	const router = useRouter();
	const {
		token: {
			colorPrimary,
			colorPrimaryBg,
			colorBgContainer,
			colorText,
		},
	} = theme.useToken();
	const user = useUserStore((state: any)  => state.nombre);
	const entity = useUserStore((state: any)  => state.entidadSeleccionada.nombre);
	// Modal.useModal() (no el estatico Modal.confirm) para que el dialogo de confirmacion herede
	// el dsTheme del ConfigProvider local del Navbar (02_lineamientos_visuales.md, AB#11021) --
	// los metodos estaticos de antd se montan fuera del arbol de React y no heredan el theme de
	// un ConfigProvider que no sea global.
	const [modal, contextHolder] = Modal.useModal();

	useEffect(() => {
		try {
			const token = jwtDecode(localStorage.getItem('token') || '') as {
				nombre: string;
			};
			setUsuario(token.nombre);
		} catch (error) {		
		}
	}, []); 

	const confirmLogout = () =>
		new Promise<boolean>((resolve) => {
			modal.confirm({
				// El icono via la prop `icon` de Modal.confirm se ve desalineado con el titulo: el
				// CSS de antd (float + margin para acomodar titulo/contenido al lado del icono) esta
				// condicionado a la clase `.anticon` de sus propios iconos SVG, que este <span> de
				// Material Symbols no tiene -- ese offset automatico nunca se aplica. En vez de pelear
				// contra ese mecanismo implicito, se arma el icono+titulo a mano con flexbox (icon:
				// null desactiva la indentacion que antd le da al resto del body cuando hay icono, asi
				// que title/content quedan alineados entre si tambien).
				icon: null,
				title: (
					<span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
						{/* dsTheme.token.colorPrimary (no la variable colorPrimary de arriba): ese
						    theme.useToken() se ejecuta antes del ConfigProvider local que este
						    componente devuelve, asi que en realidad lee el theme GLOBAL (#0886CF,
						    WithThemes/default-light.tsx), no el dsTheme (#1798D5) que el boton
						    "Aceptar" de este modal si hereda correctamente via Modal.useModal() +
						    contextHolder anidado dentro del ConfigProvider local (AB#11021). */}
						<span style={{ display: 'flex', color: dsTheme.token?.colorPrimary }}>
							{Icon('logout', 20)}
						</span>
						Cerrar Sesión
					</span>
				),
				content: '¿Estás seguro de que quieres cerrar sesión?',
				okText: 'Aceptar',
				cancelText: 'Cancelar',
				centered: true,
				onOk: () => resolve(true),
				onCancel: () => resolve(false),
			});
		});

	const onClickLogout: MenuProps['onClick'] = async () => {
		const confirmed = await confirmLogout();

		if (!confirmed) return;

		sessionStorage.clear();
		localStorage.clear();
		// localStorage.setItem("theme", "light")
		router.push('/sign-in');
	};	
	const onChangeEntidad = () => {
		const token = localStorage.getItem('token');

		localStorage.clear();

		if (token) {
			localStorage.setItem('token', token);
		}
		router.push('/entidad-seleccion');
	};

	const items: MenuProps['items'] = [
		{
			label: <a>Cerrar Sesión</a>,
			key: '0',
			icon: Icon('logout', 16),
			onClick: onClickLogout,
		},
		{
			label: <a>Cambiar Entidad</a>,
			key: '1',
			icon: Icon('swap_horiz', 16),
			onClick: onChangeEntidad,
		},
	];

	return (
		// Design System Soluciones Andinas (02_lineamientos_visuales.md, AB#10842): el navbar es
		// infra compartida por toda la app (se monta en cada pagina), mismo criterio que el Sidebar.
		<ConfigProvider theme={dsTheme}>
		{contextHolder}
		<Header
			style={{
				padding: '16px 24px',
				margin: 0,
				position: 'sticky',
				top: 0,
				zIndex: 1,
				width: '100%',
				display: 'flex',
				alignItems: 'center',
				background: colorBgContainer,
				// Altura fija igual a SIDEBAR_HEADER_HEIGHT (Sidebar.tsx) con boxSizing border-box:
				// sin esto, el Header mide alto+padding+borde (~65px, variable segun el contenido) en
				// vez de los 64px exactos del bloque del logo del Sidebar, y el borde inferior de
				// ambos queda a distinta altura -- se nota como un escalon justo donde se cruzan con
				// la linea vertical (AB#10842).
				height: '64px',
				boxSizing: 'border-box',
				// #E5E5E5 calcado por muestreo de pixeles de ejemploPantalla.png (AB#10842) -- mismo
				// gris que la linea vertical/horizontal del Sidebar, para que las tres formen una
				// sola linea continua.
				borderBottom: '1px solid #E5E5E5',
				// antd Layout.Header trae line-height fijo en 64px por clase; sin este override se
				// hereda por los <span> de texto de adentro (el de la pildora no tenia un lineHeight
				// propio que lo pisara) e inflaba su caja de linea a 64px, haciendo que la pildora
				// sobresalga del navbar. Mismo fix que ya se aplico en WorkflowBuilder.tsx (AB#10842).
				lineHeight: 'normal',
			}}
		>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					width: '100%',
				}}
			>
				{/* Selector de entidad (AB#10842): antes vivia como subtitulo chico bajo el nombre
				    del admin; ahora es su propia pildora a la izquierda. Colores calcados por
				    muestreo de pixeles de la imagen de referencia del usuario (ejemploPantalla.png):
				    fondo #EAF7FD (color plano, no un tinte con opacidad), texto #1577A6 semibold,
				    icono como triangulo solido en el primario de marca -- no el isotipo completo
				    con degrade, que ahi se ve como una figura plana de un solo color. */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						background: '#EAF7FD',
						borderRadius: 999,
						padding: '7px 16px 7px 12px',
						lineHeight: 'normal',
						cursor: 'pointer',
					}}
					onClick={onChangeEntidad}
				>
					<svg width={14} height={14} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
						<path fill={colorPrimary} d="M12 4L21 20H3L12 4Z" />
					</svg>
					<span
						style={{ fontSize: 14, fontWeight: 600, lineHeight: 'normal', color: '#1577A6' }}
						title={entity}
					>
						{entity}
					</span>
				</div>

				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						margin: '4px 0',
						overflow: 'hidden',
					}}
				>
					{/* <LocaleSwitcher/> */}
					{/* Boton de modo oscuro deshabilitado a pedido explicito (AB#10842) -- se deja
					    comentado, no eliminado, para poder reactivarlo mas adelante. */}
					{/* <ThemeToggle /> */}
					<Dropdown menu={{ items }} trigger={['click']}>
  <a onClick={(e) => e.preventDefault()} style={{ marginRight: 15, display: 'flex', alignItems: 'center' }}>
    <Avatar
      style={{
        color: colorPrimary,
        backgroundColor: colorPrimaryBg,
        marginRight: 8,
      }}
      icon={Icon('person', 18)}
    />
    {/* El nombre de la entidad ya se muestra en su propia pildora a la izquierda del navbar
        (AB#10842) -- este bloque ya no lo repite como subtitulo. */}
    <span
      style={{
        color: colorText,
        fontSize: '14px',
        lineHeight: '1.2',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
      title={user}
    >
      {user}
    </span>
  </a>
</Dropdown>
				</div>
			</div>
		</Header>
		</ConfigProvider>
	);
};

export default DashboardNavbar;

const ThemeToggle: React.FC = () => {
	const { setTheme, theme } = useTheme();

	const toggleTheme = () => {
		const newTheme = theme === 'light' ? 'dark' : 'light';
		setTheme(newTheme);
	};

	return (
		<div
			className="theme-toggle"
			style={{ cursor: 'pointer' }}
			onClick={toggleTheme}
		>
			<span style={{ marginRight: 15, display: 'flex' }}>
				{theme === 'system' ? (
					window.matchMedia('(prefers-color-scheme: dark)').matches ? (
						Icon('dark_mode', 20)
					) : (
						Icon('light_mode', 20)
					)
				) : theme === 'light' ? (
					Icon('light_mode', 20)
				) : (
					Icon('dark_mode', 20)
				)}
			</span>
		</div>
	);
};
